import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const inputCls =
  "w-full rounded-md border border-[#D8CDBC] bg-[#FDFBF7] px-3 py-2 text-sm text-[#292725] placeholder:text-[#8A7E70] focus:border-[#C27803] focus:outline-none focus:ring-2 focus:ring-[#C27803]/30";

export default function PhotosTab({ onChanged }) {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/gallery").then((r) => setPhotos(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a photo first");
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      data.append("caption", caption);
      await api.post("/gallery", data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added to the gallery");
      setCaption("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const saveCaption = async (photo, newCaption) => {
    try {
      await api.patch(`/gallery/${photo.id}`, { caption: newCaption });
      toast.success("Caption saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (photo) => {
    if (!window.confirm("Take this photo out of the gallery?")) return;
    try {
      await api.delete(`/gallery/${photo.id}`);
      toast.success("Photo removed");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-photos-tab">
      <div className="rounded-md border border-[#D8CDBC] bg-[#FDFBF7] p-6 shadow-sm">
        <h2 className="font-serif text-xl font-semibold text-[#292725]">Hang a new photo</h2>
        <p className="mt-1 text-sm text-[#8A7E70]">
          Photos you add here appear in the "Step Inside the Shop" gallery on the home page.
        </p>
        <form onSubmit={upload} className="mt-5 grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Photo *</label>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              data-testid="photo-upload-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full rounded-md border border-[#D8CDBC] bg-[#FDFBF7] px-3 py-1.5 text-sm text-[#292725] file:mr-3 file:rounded-sm file:border-0 file:bg-[#C27803] file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-[#FDFBF7]"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Caption</label>
            <input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              data-testid="photo-caption-input"
              placeholder="e.g. the front counter, dressed for fall"
              className={inputCls}
            />
          </div>
          <button
            type="submit"
            data-testid="photo-upload-button"
            disabled={uploading}
            className="flex items-center justify-center gap-2 rounded-md bg-[#C27803] px-5 py-2.5 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#9A5E02] disabled:opacity-60"
          >
            <ImagePlus className="h-4 w-4" /> {uploading ? "Hanging..." : "Add to Gallery"}
          </button>
        </form>
      </div>

      {photos.length === 0 ? (
        <div className="mt-5 rounded-md border border-dashed border-[#D8CDBC] bg-[#FDFBF7] py-16 text-center" data-testid="photos-empty-state">
          <p className="font-serif text-xl text-[#292725]">No photos on the wall yet</p>
          <p className="mt-1 text-sm text-[#8A7E70]">Hang your first shop photo above.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-photos-grid">
          {photos.map((p) => (
            <div key={p.id} className="overflow-hidden rounded-md border border-[#D8CDBC] bg-[#FDFBF7] shadow-sm" data-testid={`admin-photo-${p.id}`}>
              <img src={p.url} alt={p.caption || "Shop photo"} className="h-48 w-full object-cover" />
              <div className="flex items-center gap-2 p-3">
                <input
                  defaultValue={p.caption}
                  data-testid={`photo-caption-edit-${p.id}`}
                  onBlur={(e) => e.target.value !== p.caption && saveCaption(p, e.target.value)}
                  placeholder="Add a caption..."
                  className="min-w-0 flex-1 rounded-sm border border-transparent bg-transparent px-1 py-1 font-hand text-lg text-[#5C554B] focus:border-[#C27803] focus:outline-none"
                />
                <button
                  onClick={() => remove(p)}
                  data-testid={`photo-delete-${p.id}`}
                  className="rounded-md border border-[#D8CDBC] p-2 text-[#5C554B] transition-colors hover:border-[#8C251C] hover:text-[#8C251C]"
                  aria-label="Remove photo"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
